/**
 * Drawing the inputs a card asks for, and deciding when they are answerable.
 *
 * 🔴 EVERY CONTROL HERE IS BUILT WITH createElement AND textContent, NEVER
 *    innerHTML. Labels and descriptions come from the customer's verb.md and a
 *    filename comes from a user's own disk, which makes both untrusted strings
 *    that end up inside their page. The same rule the message renderer follows.
 *
 * docs/40 sections 5.1 to 5.4.
 */
import { register, previewUrl, type FileHandleMeta } from "./files.js";

export interface InputFieldSpec {
  readonly name: string;
  readonly label: string;
  readonly type:
    | "text"
    | "textarea"
    | "email"
    | "url"
    | "date"
    | "tel"
    | "number"
    | "boolean"
    | "select"
    | "radio"
    | "file";
  readonly required: boolean;
  readonly description?: string;
  readonly options?: readonly string[];
  readonly pattern?: string;
  readonly maxLength?: number;
  readonly minLength?: number;
  readonly maximum?: number;
  readonly minimum?: number;
  /** A whole number: seats, quantities, counts. The input refuses a letter or a decimal point. */
  readonly integer?: boolean;
  readonly accept?: readonly string[];
  readonly maxBytes?: number;
  readonly value?: unknown;
}

export interface FieldSet {
  readonly element: HTMLElement;
  /** Current values, ready to post. Files appear as their handle. */
  values(): Record<string, unknown>;
  /** The first problem, or null when every required field is answerable. */
  firstProblem(): string | null;
  /** Freezes every control. Called the moment a decision is taken. */
  lock(): void;
  /** Focuses the first control, so somebody can start typing immediately. */
  focusFirst(): void;
}

/**
 * How many fields one step shows, by where the card is drawn.
 *
 * 🔴 A COUNT, DECIDED BY VISHNU 2026-09-21 AFTER SEEING THE CARD: three in the
 *    side panel, five in the dialog. The first build used a height budget, and
 *    on screen the plainer rule is the one a person can predict. It stays safe
 *    because the tall control it was guarding against, a radio group with many
 *    options, cannot occur: five or more options is a select (fields.ts).
 */
export const FIELDS_PER_STEP = { panel: 3, dialog: 5 } as const;

/** Splits fields into steps of at most `perStep`. */
export function intoSteps(fields: readonly InputFieldSpec[], perStep: number): InputFieldSpec[][] {
  const size = Math.max(1, perStep);
  const steps: InputFieldSpec[][] = [];
  for (let i = 0; i < fields.length; i += size) steps.push(fields.slice(i, i + size));
  return steps.length > 0 ? steps : [[]];
}

export interface Stepper extends FieldSet {
  /** True when the card has more than one step of fields. */
  readonly multi: boolean;
  /** True on the final, review step, the only one that may confirm. */
  onReview(): boolean;
  /** The first problem on the step being shown, which is what Next checks. */
  stepProblem(): string | null;
  next(): void;
  back(): void;
  /** Zero-based position, and the total including the review step. */
  position(): { index: number; total: number };
}

/**
 * Fields in steps, ending on a review of everything.
 *
 * 🔴 THE LAST STEP IS ALWAYS THE WHOLE PICTURE. A person cannot approve an
 *    action whose arguments they only ever saw three at a time, so after the
 *    field steps comes one that lists every value, typed and known, above the
 *    Confirm button, and Confirm exists only there. docs/40 section 5.4.
 *
 * With one step's worth of fields there is no review step: the card already
 * shows everything at once, which is exactly what it would say.
 */
export function buildStepper(
  fields: readonly InputFieldSpec[],
  perStep: number,
  known: Record<string, unknown>,
  onChange: () => void,
  describe: (name: string, value: unknown) => string,
): Stepper {
  const chunks = intoSteps(fields, perStep);
  const sets = chunks.map((chunk) => buildFields(chunk, onChange));
  const multi = sets.length > 1;

  const element = document.createElement("div");
  element.className = "vstepper";

  /**
   * One segment per step, filled up to the current one, plus a label that says
   * the same thing in words. The bar is what reads at a glance; the words are
   * what a screen reader and a colourblind reader get, so neither carries the
   * meaning alone.
   */
  const progress = document.createElement("div");
  progress.className = "vstep-progress";
  const progressLabel = document.createElement("p");
  progressLabel.className = "vstep-label";
  progressLabel.setAttribute("aria-live", "polite");
  const bar = document.createElement("div");
  bar.className = "vstep-bar";
  progress.append(progressLabel, bar);

  const review = document.createElement("dl");
  review.className = "confirm-args vstep-review";

  if (multi) element.appendChild(progress);
  for (const set of sets) element.appendChild(set.element);
  if (multi) element.appendChild(review);

  // Index into sets, or sets.length for the review step.
  let index = 0;
  const total = multi ? sets.length + 1 : 1;

  const segments: HTMLElement[] = [];
  for (let i = 0; i < total; i++) {
    const seg = document.createElement("span");
    seg.className = "vstep-seg";
    bar.appendChild(seg);
    segments.push(seg);
  }

  const labelOf = new Map(fields.map((f) => [f.name, f.label]));

  const all = () => {
    const out: Record<string, unknown> = {};
    for (const set of sets) Object.assign(out, set.values());
    return out;
  };

  const renderReview = () => {
    review.replaceChildren();
    const rows: [string, unknown][] = [...Object.entries(known), ...Object.entries(all())];
    for (const [name, value] of rows) {
      const row = document.createElement("div");
      const dt = document.createElement("dt");
      dt.textContent = labelOf.get(name) ?? name;
      const dd = document.createElement("dd");
      dd.textContent = describe(name, value);
      row.append(dt, dd);
      review.appendChild(row);
    }
  };

  const show = () => {
    sets.forEach((set, i) => {
      set.element.hidden = multi && i !== index;
    });
    if (multi) {
      review.hidden = index !== sets.length;
      if (index === sets.length) renderReview();
      progressLabel.textContent =
        index === sets.length ? `Step ${total} of ${total} · Review` : `Step ${index + 1} of ${total}`;
      segments.forEach((seg, i) => {
        seg.dataset["state"] = i < index ? "done" : i === index ? "current" : "todo";
      });
    }
  };
  show();

  return {
    element,
    multi,
    values: all,
    firstProblem() {
      for (const set of sets) {
        const p = set.firstProblem();
        if (p) return p;
      }
      return null;
    },
    lock() {
      for (const set of sets) set.lock();
    },
    focusFirst() {
      sets[Math.min(index, sets.length - 1)]?.focusFirst();
    },
    onReview() {
      return !multi || index === sets.length;
    },
    stepProblem() {
      return index < sets.length ? (sets[index]?.firstProblem() ?? null) : null;
    },
    next() {
      if (index < sets.length && !this.stepProblem()) {
        index += 1;
        show();
        if (index < sets.length) sets[index]?.focusFirst();
      }
    },
    back() {
      if (index > 0) {
        index -= 1;
        show();
        sets[index]?.focusFirst();
      }
    },
    position() {
      return { index, total };
    },
  };
}

/**
 * The characters a field may contain at all, or null for free text.
 *
 * A minus sign only where the author allowed a negative, so "min 1" on a seat
 * count cannot even be typed below zero.
 */
function allowedChars(f: InputFieldSpec): RegExp | null {
  if (f.type === "tel") return /^[0-9+()\s.-]+$/;
  if (f.type !== "number") return null;
  const minus = typeof f.minimum === "number" && f.minimum >= 0 ? "" : "-";
  return f.integer ? new RegExp(`^[0-9${minus}]+$`) : new RegExp(`^[0-9.${minus}]+$`);
}

function refusedMessage(f: InputFieldSpec): string {
  if (f.type === "tel") return `${f.label} takes digits, spaces and + ( ) -.`;
  return f.integer ? `${f.label} takes whole numbers only.` : `${f.label} takes numbers only.`;
}

function humanBytes(n: number): string {
  if (n >= 1024 * 1024) return `${Math.round(n / (1024 * 1024))}MB`;
  return `${Math.round(n / 1024)}KB`;
}

function isImageOnly(accept: readonly string[] | undefined): boolean {
  return !!accept?.length && accept.every((a) => a.startsWith("image/"));
}

/** "Images up to 2MB", said in the words a person uses rather than mime types. */
function acceptSummary(accept: readonly string[] | undefined, maxBytes: number | undefined): string {
  const kinds = (accept ?? []).map((a) =>
    a.startsWith("image/") ? "Images" : a === "application/pdf" ? "PDF" : a === "text/csv" ? "CSV" : a,
  );
  const what = kinds.length ? [...new Set(kinds)].join(", ") : "Any file";
  const size = typeof maxBytes === "number" ? ` up to ${humanBytes(maxBytes)}` : "";
  return `${what}${size}, or drop it here`;
}

/** Built from nodes, not a string, for the same reason nothing here uses innerHTML. */
function uploadIcon(): SVGSVGElement {
  const NS = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(NS, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("class", "vfield-drop-icon");
  for (const d of ["M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4", "M17 8l-5-5-5 5", "M12 3v12"]) {
    const path = document.createElementNS(NS, "path");
    path.setAttribute("d", d);
    svg.appendChild(path);
  }
  return svg;
}

export function buildFields(fields: readonly InputFieldSpec[], onChange: () => void): FieldSet {
  const wrap = document.createElement("div");
  wrap.className = "vfields";

  const controls = new Map<string, () => unknown>();
  const problems = new Map<string, () => string | null>();
  const locks: (() => void)[] = [];
  let firstControl: HTMLElement | null = null;

  for (const f of fields) {
    const row = document.createElement("div");
    row.className = "vfield";
    row.dataset["name"] = f.name;

    const label = document.createElement("label");
    label.className = "vfield-label";
    label.textContent = f.required ? f.label : `${f.label} (optional)`;
    row.appendChild(label);

    if (f.description) {
      const hint = document.createElement("p");
      hint.className = "vfield-hint";
      hint.textContent = f.description;
      row.appendChild(hint);
    }

    const error = document.createElement("p");
    error.className = "vfield-error";
    error.setAttribute("role", "alert");

    const announce = (message: string | null) => {
      error.textContent = message ?? "";
      row.dataset["invalid"] = message ? "true" : "false";
    };

    if (f.type === "radio" && f.options) {
      const group = document.createElement("div");
      group.className = "vfield-radios";
      group.setAttribute("role", "radiogroup");
      const name = `vf_${f.name}_${Math.random().toString(36).slice(2, 7)}`;
      let selected: string | null = null;

      for (const option of f.options) {
        const item = document.createElement("label");
        item.className = "vfield-radio";
        const input = document.createElement("input");
        input.type = "radio";
        input.name = name;
        input.value = option;
        input.addEventListener("change", () => {
          selected = option;
          announce(null);
          onChange();
        });
        const text = document.createElement("span");
        text.textContent = option;
        item.append(input, text);
        group.appendChild(item);
        locks.push(() => (input.disabled = true));
        firstControl ??= input;
      }
      row.appendChild(group);
      controls.set(f.name, () => selected ?? undefined);
      problems.set(f.name, () => (f.required && !selected ? `${f.label} is required.` : null));
    } else if (f.type === "select" && f.options) {
      const select = document.createElement("select");
      select.className = "vfield-input";
      const blank = document.createElement("option");
      blank.value = "";
      blank.textContent = "Choose one";
      select.appendChild(blank);
      for (const option of f.options) {
        const el = document.createElement("option");
        el.value = option;
        el.textContent = option;
        select.appendChild(el);
      }
      select.addEventListener("change", () => {
        announce(null);
        onChange();
      });
      row.appendChild(select);
      locks.push(() => (select.disabled = true));
      firstControl ??= select;
      controls.set(f.name, () => select.value || undefined);
      problems.set(f.name, () => (f.required && !select.value ? `${f.label} is required.` : null));
    } else if (f.type === "boolean") {
      const item = document.createElement("label");
      item.className = "vfield-check";
      const input = document.createElement("input");
      input.type = "checkbox";
      input.addEventListener("change", onChange);
      const text = document.createElement("span");
      text.textContent = f.label;
      item.append(input, text);
      // The label above would repeat it, so the standalone one is dropped.
      label.remove();
      row.insertBefore(item, row.firstChild);
      locks.push(() => (input.disabled = true));
      firstControl ??= input;
      controls.set(f.name, () => input.checked);
      problems.set(f.name, () => null);
    } else if (f.type === "file") {
      /**
       * 🔴 THE NATIVE INPUT STAYS, VISUALLY HIDDEN, AND NEVER display:none. It
       *    is what opens the picker, what a keyboard focuses and what a screen
       *    reader announces. The "Choose File" button it draws cannot be styled
       *    and reads as a different product inside the card, so a label drawn
       *    in the widget's own tokens sits on top of it and forwards the click.
       */
      const input = document.createElement("input");
      input.type = "file";
      input.className = "vfield-file-native";
      input.id = `vf_${f.name}_${Math.random().toString(36).slice(2, 7)}`;
      if (f.accept?.length) input.accept = f.accept.join(",");

      const zone = document.createElement("label");
      zone.className = "vfield-drop";
      zone.htmlFor = input.id;
      zone.appendChild(uploadIcon());
      const zoneText = document.createElement("span");
      zoneText.className = "vfield-drop-text";
      const zoneMain = document.createElement("span");
      zoneMain.className = "vfield-drop-main";
      zoneMain.textContent = isImageOnly(f.accept) ? "Choose an image" : "Choose a file";
      const zoneSub = document.createElement("span");
      zoneSub.className = "vfield-drop-sub";
      zoneSub.textContent = acceptSummary(f.accept, f.maxBytes);
      zoneText.append(zoneMain, zoneSub);
      zone.appendChild(zoneText);

      // Dropping a file does the same as picking one: it is assigned to the
      // real input and the same change handler runs, so there is one path.
      zone.addEventListener("dragover", (e) => {
        e.preventDefault();
        zone.dataset["drag"] = "true";
      });
      zone.addEventListener("dragleave", () => delete zone.dataset["drag"]);
      zone.addEventListener("drop", (e) => {
        e.preventDefault();
        delete zone.dataset["drag"];
        if (input.disabled || !e.dataTransfer?.files.length) return;
        input.files = e.dataTransfer.files;
        input.dispatchEvent(new Event("change"));
      });

      const chosen = document.createElement("div");
      chosen.className = "vfield-file";
      chosen.hidden = true;
      let meta: FileHandleMeta | null = null;

      const clearChoice = () => {
        input.value = "";
        meta = null;
        chosen.replaceChildren();
        chosen.hidden = true;
        zone.hidden = false;
        onChange();
      };

      input.addEventListener("change", () => {
        chosen.replaceChildren();
        chosen.hidden = true;
        zone.hidden = false;
        meta = null;
        const file = input.files?.[0];
        if (!file) {
          onChange();
          return;
        }

        /**
         * 🔴 SIZE IS CHECKED AT SELECTION, NOT AFTER THE UPLOAD. Somebody who
         *    picks a 40MB file learns now, rather than after a slow upload ends
         *    in a 413 they cannot act on. docs/40 section 5.7.
         */
        if (typeof f.maxBytes === "number" && file.size > f.maxBytes) {
          input.value = "";
          announce(`That file is ${humanBytes(file.size)}. The limit is ${humanBytes(f.maxBytes)}.`);
          onChange();
          return;
        }

        announce(null);
        meta = register(file);

        const preview = previewUrl(meta.handle);
        if (preview) {
          const img = document.createElement("img");
          img.className = "vfield-file-thumb";
          img.src = preview;
          img.alt = "";
          chosen.appendChild(img);
        } else {
          const badge = document.createElement("span");
          badge.className = "vfield-file-badge";
          badge.textContent = (file.name.split(".").pop() ?? "file").slice(0, 4).toUpperCase();
          chosen.appendChild(badge);
        }

        const text = document.createElement("span");
        text.className = "vfield-file-text";
        const name = document.createElement("span");
        name.className = "vfield-file-name";
        name.textContent = meta.name;
        const size = document.createElement("span");
        size.className = "vfield-file-size";
        size.textContent = humanBytes(meta.size);
        text.append(name, size);
        chosen.appendChild(text);

        const remove = document.createElement("button");
        remove.type = "button";
        remove.className = "vfield-file-remove";
        remove.textContent = "Remove";
        remove.setAttribute("aria-label", `Remove ${meta.name}`);
        remove.addEventListener("click", clearChoice);
        chosen.appendChild(remove);

        chosen.hidden = false;
        zone.hidden = true;
        onChange();
      });

      row.append(input, zone, chosen);
      locks.push(() => {
        input.disabled = true;
        chosen.querySelector("button")?.remove();
      });
      firstControl ??= input;
      // 🔴 The HANDLE travels, never the bytes. files.ts holds the File.
      controls.set(f.name, () => meta?.handle);
      problems.set(f.name, () => (f.required && !meta ? `${f.label} is required.` : null));
    } else {
      const input =
        f.type === "textarea"
          ? document.createElement("textarea")
          : document.createElement("input");
      input.className = "vfield-input";
      if (input instanceof HTMLInputElement) {
        /**
         * 🔴 A NUMBER IS A TEXT INPUT WITH A NUMERIC KEYPAD, NOT type="number".
         *    type="number" lets "e", "+" and "." through in Chrome and any
         *    letter at all in Firefox, then reports the value as empty, so a
         *    seat count of "two" became "Seats is required". The filter below
         *    refuses the keystroke instead, in every browser.
         */
        input.type =
          f.type === "number"
            ? "text"
            : f.type === "email"
              ? "email"
              : f.type === "url"
                ? "url"
                : f.type === "date"
                  ? "date"
                  : f.type === "tel"
                    ? "tel"
                    : "text";
        if (f.type === "number") input.inputMode = f.integer ? "numeric" : "decimal";
        const allowed = allowedChars(f);
        if (allowed) {
          input.addEventListener("beforeinput", (e) => {
            if (e.data && !allowed.test(e.data)) {
              e.preventDefault();
              announce(refusedMessage(f));
            }
          });
          // Paste and autofill skip beforeinput's data in some browsers, so the
          // value itself is cleaned too. Never silently changes a valid value.
          input.addEventListener("input", () => {
            const cleaned = [...input.value].filter((ch) => allowed.test(ch)).join("");
            if (cleaned !== input.value) {
              input.value = cleaned;
              announce(refusedMessage(f));
            }
          });
        }
      }
      if (typeof f.maxLength === "number") input.maxLength = f.maxLength;
      if (typeof f.value === "string") input.value = f.value;

      input.addEventListener("input", () => {
        announce(null);
        onChange();
      });
      row.appendChild(input);
      locks.push(() => (input.readOnly = true));
      firstControl ??= input;

      controls.set(f.name, () => {
        const raw = input.value.trim();
        if (!raw) return undefined;
        return f.type === "number" ? Number(raw) : raw;
      });

      problems.set(f.name, () => {
        const raw = input.value.trim();
        if (!raw) return f.required ? `${f.label} is required.` : null;
        /**
         * Every message says what would be right, never just "Invalid". A
         * person cannot act on a rejection that does not describe the target.
         */
        if (f.type === "email" && !/^[^@\s]+@[^@\s.]+\.[^@\s]+$/.test(raw)) {
          return `${f.label} needs to look like name@company.com.`;
        }
        if (f.type === "url" && !/^https?:\/\/[^\s]+$/i.test(raw)) {
          return `${f.label} needs to start with https://.`;
        }
        if (f.type === "date" && !/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
          return `${f.label} needs to look like 2026-09-19.`;
        }
        if (f.type === "number") {
          const n = Number(raw);
          if (!Number.isFinite(n)) return `${f.label} must be a number.`;
          if (f.integer && !Number.isInteger(n)) return `${f.label} must be a whole number.`;
          if (typeof f.minimum === "number" && n < f.minimum) {
            return `${f.label} cannot be less than ${f.minimum}.`;
          }
          if (typeof f.maximum === "number" && n > f.maximum) {
            return `${f.label} cannot be more than ${f.maximum}.`;
          }
        }
        if (typeof f.minLength === "number" && raw.length < f.minLength) {
          return `${f.label} needs at least ${f.minLength} characters.`;
        }
        if (f.pattern) {
          try {
            if (!new RegExp(f.pattern).test(raw)) return `${f.label} is not in the expected format.`;
          } catch {
            // A pattern the author wrote that does not compile must not silently
            // accept everything; the server refuses it too.
            return `${f.label} has an invalid pattern configured.`;
          }
        }
        return null;
      });
    }

    row.appendChild(error);
    wrap.appendChild(row);
  }

  return {
    element: wrap,
    values() {
      const out: Record<string, unknown> = {};
      for (const [name, read] of controls) {
        const value = read();
        if (value !== undefined) out[name] = value;
      }
      return out;
    },
    firstProblem() {
      for (const [, check] of problems) {
        const problem = check();
        if (problem) return problem;
      }
      return null;
    },
    lock() {
      for (const fn of locks) fn();
    },
    focusFirst() {
      firstControl?.focus();
    },
  };
}
